import classnames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { vn } from 'vend-number'
import { getCurrencyFormatParts } from '@vend/utilities'
import {
  focusGlobalSearch,
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { displayRound } from '../../../filters/display-round'
import { ERRORS } from '../../../constants'
import { useFormatNumber } from '../../helpers/formatNumberHooks'

interface Props {
  allowedPrefix?: string
  allowedSuffix?: string
  className: string
  error?: string | null
  decimalPlaces?: number
  min?: number | string
  max?: number | string
  onError: (errors: string | null) => void
  onValueChange: (value: string) => void
  trimInsignificantDigits?: boolean
  value: number | string
  onFocusChange: (value: string) => void
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  /**
   * When set, calls to onValueChange are debounced by this many milliseconds
   * (flushed on blur/unmount), and incomplete intermediate inputs ("", ".", "-")
   * are not committed while typing. Leave unset for immediate commits.
   */
  commitDelayMs?: number
}

const SYSTEM_DECIMAL_SEPARATOR = '.'

/**
 * Commit delay for inputs whose changes trigger a full sale recalculation.
 * Long enough to coalesce rapid typing into a single commit, short enough to
 * feel immediate once the user pauses.
 */
export const LINE_ITEM_COMMIT_DELAY_MS = 300

/**
 * Model -> View: Format given model input value and return the correct display value taking configured
 * options into account, e.g. decimalPlaces & trimInsignificant digits settings.
 *
 * @param input
 *        A vend number input value to format for display.
 *
 * @return The given vend number input formatted for display.
 */
const formatVendNumber = (
  input: number | string,
  {
    decimalPlaces,
    trimInsignificantDigits,
  }: Pick<Props, 'decimalPlaces' | 'trimInsignificantDigits'>
) => {
  let formattedNumber = input
  if (input != null) {
    formattedNumber = String(input)
  }

  if (formattedNumber) {
    formattedNumber = displayRound(
      formattedNumber,
      decimalPlaces,
      trimInsignificantDigits
    )
  }

  return formattedNumber
}

/**
 * Strip the allowed prefix/suffix (and a leading minus sign) off a display
 * input value.
 */
const stripAffixes = (
  input: number | string,
  {
    allowedPrefix,
    allowedSuffix,
  }: Pick<Props, 'allowedPrefix' | 'allowedSuffix'>
) => {
  input = String(input).trim()
  let negative = false
  if (input.startsWith('-')) {
    negative = true
    input = input.slice(1)
  }

  if (allowedPrefix && input.startsWith(allowedPrefix)) {
    input = input.slice(allowedPrefix.length)
    // Allow negative to be either before or after the prefix.
    if (!negative && input.startsWith('-')) {
      negative = true
      input = input.slice(1)
    }
  }

  // allowedSuffix alone is invalid because you do not type suffix first.
  if (
    allowedSuffix &&
    input.length > allowedSuffix.length &&
    input.endsWith(allowedSuffix)
  ) {
    input = input.slice(0, -allowedSuffix.length)
  }

  return { stripped: input, negative }
}

/**
 * Normalize given display input value (remove any allowed characters).
 *
 * There are special cases implemented for when the input is currently focused (i.e. we can assume from
 * this that the user is still typing) whereby certain usually invalid cases are interpreted as 0 for
 * example to ensure that calculations and validity do not break prematurely to a valid number being finished
 * (e.g. in cases of slow typing and such).
 *
 * @param input A number input value to parse and round for display.
 *
 * @return The normalized input.
 */
const normalizeNumber = (
  input: number | string,
  options: Pick<Props, 'allowedPrefix' | 'allowedSuffix'>
) => {
  const { stripped, negative } = stripAffixes(input, options)
  let normalized = stripped

  // Special case to handle empty input as valid while typing when empty is not allowed.
  if (!normalized.trim() || normalized === '.') {
    normalized = '0'
  }

  if (negative && normalized !== '0') {
    return '-' + normalized
  }
  return normalized
}

/**
 * An input the user is still in the middle of typing ("", ".", "-", "-."):
 * normalizeNumber coerces these to 0, which must not be committed to the sale
 * mid-typing — a slow async recalculation would overwrite the finished value
 * with that transient 0.
 */
const isIncompleteNumber = (
  input: number | string,
  options: Pick<Props, 'allowedPrefix' | 'allowedSuffix'>
) => {
  const { stripped } = stripAffixes(input, options)
  return !stripped.trim() || stripped === '.'
}

/**
 * Ensure the value is a valid number. This is instead of setting the input to be a number, because this
 * doesn't allow us to enforce decimal places.
 *
 * Also enforce minimum and maximum, if specified.
 *
 * @param value
 *        The value to validate (has been parsed).
 *
 * @return Returns true if given value is a valid number, otherwise false.
 */
const validate = (
  value: string,
  { max, min }: Pick<Props, 'max' | 'min'>
): string | null => {
  const numberVal = Number(value)

  if (Number.isNaN(numberVal)) {
    return ERRORS.INVALID_CHARACTERS
  }
  if (min !== undefined && vn(numberVal).lt(min)) {
    return ERRORS.INVALID_MIN
  }
  if (max !== undefined && vn(numberVal).gt(max)) {
    return ERRORS.INVALID_MAX
  }
  return null
}

export const testables = { normalizeNumber, isIncompleteNumber }

const handleEnter: React.KeyboardEventHandler = event => {
  if (event.key === 'Enter') {
    focusGlobalSearch()
  }
}

/**
 * An input component used for line item quantity, price, and discount.
 */
export const LineItemInput = React.forwardRef(
  (props: Props, ref: React.Ref<HTMLInputElement>) => {
    const {
      className,
      error,
      onError,
      onValueChange,
      onFocusChange,
      onFocus: customOnFocus,
      value,
      commitDelayMs,
    } = props

    // The debounced committer must survive re-renders but always call the
    // latest onValueChange (consumers recreate it per render with a fresh
    // lineItem closure).
    const latestOnValueChange = useRef(onValueChange)
    latestOnValueChange.current = onValueChange

    const debouncedCommit = useMemo(
      () =>
        debounce(
          (normalized: string) => latestOnValueChange.current(normalized),
          commitDelayMs
        ),
      [commitDelayMs]
    )

    // An unmounted input may belong to a removed line item, so only blur may
    // commit a pending edit.
    useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit])

    const commitValue = (normalized: string) => {
      if (commitDelayMs) {
        debouncedCommit(normalized)
      } else {
        onValueChange(normalized)
      }
    }

    const retailer = useRetailerSettingsManager().get()
    const activeOutlet = useRegisterManager().getActiveOutlet()
    const formatNumber = useFormatNumber({
      maximumFractionDigits: props.decimalPlaces,
      trimInsignificantDigits: props.trimInsignificantDigits,
    })

    const [focused, setFocused] = useState(false)

    const [{ inputValue, validationError }, setInputValue] = useState(() => ({
      inputValue: formatVendNumber(value, props),
      validationError: null as string | null,
    }))

    const decimalSeparator = useMemo(
      () =>
        getCurrencyFormatParts(
          retailer.culture,
          activeOutlet.currency,
          activeOutlet.currency_symbol
        ).decimal,
      [retailer.culture, activeOutlet.currency, activeOutlet.currency_symbol]
    )

    const valueWithSystemDecimalSeparator = useCallback(
      (value: string) => {
        if (decimalSeparator !== SYSTEM_DECIMAL_SEPARATOR) {
          return value.replace(
            new RegExp(`\\${decimalSeparator}`, 'g'),
            SYSTEM_DECIMAL_SEPARATOR
          )
        }
        return value
      },
      [decimalSeparator]
    )

    let displayValue
    if (focused || validationError) {
      displayValue = inputValue
    } else {
      displayValue = formatNumber(value)
    }

    const onChange: React.ChangeEventHandler<HTMLInputElement> = event => {
      const inputValue = valueWithSystemDecimalSeparator(event.target.value)

      const normalized = normalizeNumber(inputValue, props)
      const validationError = validate(normalized, props)
      setInputValue({ inputValue, validationError })
      if (validationError) {
        onError(validationError)
      } else {
        onError(null)
      }
      if (validationError !== ERRORS.INVALID_CHARACTERS) {
        if (commitDelayMs && isIncompleteNumber(inputValue, props)) {
          // Never commit the transient 0 that "", "." or "-" normalize to while
          // the user is still typing; also drop any pending older commit so it
          // cannot land on top of the newer (incomplete) input. The final value
          // is committed on blur.
          debouncedCommit.cancel()
        } else {
          commitValue(normalized)
        }
      }
    }

    const onBlur: React.FocusEventHandler = () => {
      setFocused(false)
      if (!validationError) {
        // Format normalized input value (eg remove '$', 2dp)
        const normalized = normalizeNumber(inputValue, props)
        if (commitDelayMs) {
          if (isIncompleteNumber(inputValue, props)) {
            // The input was left incomplete: commit its normalized value (0)
            // now, matching the immediate-commit behaviour.
            debouncedCommit.cancel()
            onValueChange(normalized)
          } else {
            // Make sure any pending debounced commit lands before the
            // onFocusChange side effects run.
            debouncedCommit.flush()
          }
        }
        const displayValue = formatNumber(normalized)
        setInputValue({
          inputValue: displayValue,
          validationError: null,
        })
        if (validationError !== ERRORS.INVALID_CHARACTERS) {
          onFocusChange(normalized)
        }
      }
    }

    const onFocus: React.FocusEventHandler<HTMLInputElement> = event => {
      setFocused(true)
      if (!validationError) {
        // Sync prop value to display value, in case someone changed it programmatically.
        const displayValue = formatNumber(value)
        setInputValue({
          inputValue: displayValue,
          validationError: null,
        })
      }

      if (customOnFocus) {
        customOnFocus(event)
      }
    }

    return (
      <input
        value={displayValue}
        className={classnames(
          'vd-input',
          'vd-input--text-align-right',
          { 'vd-error': validationError || error },
          className
        )}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        onKeyUp={handleEnter}
        ref={ref}
        autoComplete="off"
        inputMode="decimal"
        data-testid="line-item-input"
      />
    )
  }
)

LineItemInput.displayName = 'LineItemInput'
